/**
 * AdmissionsApply — multi-step family application via secure token link.
 *
 * 6 steps:
 *   1. Child — name, DOB, home language, comfort items
 *   2. Medical — allergies, conditions, dietary, doctor info
 *   3. Emergency contacts (1-3)
 *   4. Authorized pickup (1-5)
 *   5. Consents — granular photo/outdoor/medical/trip toggles
 *   6. Documents — e-sign required docs (typed name)
 *
 * Save after every step; final Review & Submit creates the actual Child
 * record server-side and notifies the parent via the existing message
 * system.
 *
 * On the public URL (no auth), the token in the path is the only key.
 */
import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from "lucide-react";
import MetaTags from "../components/MetaTags";
import Brand from "../components/Brand";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const STEPS = ["child", "medical", "emergency", "pickup", "consents", "documents"];

export default function AdmissionsApply() {
  const { t } = useI18n();
  const { token } = useParams();
  const nav = useNavigate();
  const [app, setApp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stepIdx, setStepIdx] = useState(0);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admissions/apply/${token}`);
      setApp(r.data);
      setStepIdx(Math.max(0, r.data.current_step || 0));
      // Load required docs metadata for the Documents step
      try {
        const dr = await api.get(`/admissions/apply/${token}/docs`);
        setDocs(dr.data || []);
      } catch (e) {
        // Endpoint optional; we'll fall back to required_signatures count
      }
    } catch (e) {
      const status = e?.response?.status;
      if (status === 410) {
        setError("expired");
      } else if (status === 404) {
        setError("notfound");
      } else {
        setError("generic");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (loading) return <FullScreen>{t("admissions.common.loading")}</FullScreen>;
  if (error === "expired" || error === "notfound") {
    return (
      <FullScreen data-testid="admissions-apply-expired">
        <span className="eyebrow text-[#8C6D1F]">{t("admissions.apply.expired_eyebrow")}</span>
        <h1 className="mt-3 font-display text-3xl text-[#1A1B2E]">
          {t("admissions.apply.expired_headline")}
        </h1>
        <p className="mt-3 text-sm text-[#6B7280] max-w-md">{t("admissions.apply.expired_body")}</p>
      </FullScreen>
    );
  }
  if (!app) return <FullScreen>{t("admissions.common.error_generic")}</FullScreen>;
  if (app.submitted_at) {
    return (
      <FullScreen data-testid="admissions-apply-submitted">
        <span className="eyebrow text-[#1F3A2A]">{t("admissions.apply.submitted_eyebrow")}</span>
        <h1 className="mt-3 font-display text-3xl text-[#1A1B2E]">
          {t("admissions.apply.submitted_headline")}
        </h1>
        <p className="mt-3 text-sm text-[#6B7280] max-w-md">{t("admissions.apply.submitted_body")}</p>
      </FullScreen>
    );
  }

  const step = STEPS[stepIdx];

  const saveStep = async (partial) => {
    try {
      const r = await api.post(`/admissions/apply/${token}/step`, {
        step: stepIdx,
        ...partial,
      });
      setApp(r.data);
      toast.success(t("admissions.apply.saved_just_now"));
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = t("admissions.common.error_generic");
      if (typeof raw === "string" && raw) msg = raw;
      toast.error(msg);
    }
  };

  const submit = async () => {
    try {
      const r = await api.post(`/admissions/apply/${token}/submit`);
      setApp(r.data);
      toast.success(t("admissions.apply.submitted_headline"));
    } catch (e) {
      const raw = e?.response?.data?.detail;
      let msg = t("admissions.common.error_generic");
      if (typeof raw === "string" && raw) msg = raw;
      toast.error(msg);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="admissions-apply-screen">
      <MetaTags title={t("admissions.apply.meta_title")} description={t("admissions.apply.meta_description")} />
      <header className="border-b border-[#EEF0F4] py-5 px-6 sm:px-10 flex items-center justify-between">
        <Brand size="md" dataTestId="brand-admissions-apply" />
        <span className="text-xs text-[#6B7280]">
          {t("admissions.apply.step", { n: stepIdx + 1, total: STEPS.length })}
        </span>
      </header>
      <main className="mx-auto max-w-2xl px-6 sm:px-10 py-12">
        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-8" aria-hidden="true">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={
                "h-1.5 flex-1 rounded-full transition-all " +
                (i <= stepIdx ? "bg-[#1F3A2A]" : "bg-[#EEF0F4]")
              }
            />
          ))}
        </div>

        {step === "child" && (
          <ChildStep
            data={app.data?.child || {}}
            onSave={(child) => saveStep({ child })}
            t={t}
          />
        )}
        {step === "medical" && (
          <MedicalStep
            data={app.data?.medical || {}}
            onSave={(medical) => saveStep({ medical })}
            t={t}
          />
        )}
        {step === "emergency" && (
          <ContactsStep
            data={app.data?.emergency_contacts || []}
            kind="emergency"
            onSave={(emergency_contacts) => saveStep({ emergency_contacts })}
            t={t}
          />
        )}
        {step === "pickup" && (
          <PickupStep
            data={app.data?.authorized_pickup || []}
            onSave={(authorized_pickup) => saveStep({ authorized_pickup })}
            t={t}
          />
        )}
        {step === "consents" && (
          <ConsentsStep
            data={app.data?.consents || {}}
            onSave={(consents) => saveStep({ consents })}
            t={t}
          />
        )}
        {step === "documents" && (
          <DocumentsStep
            app={app}
            token={token}
            onSigned={load}
            t={t}
          />
        )}

        {/* Footer nav */}
        <div className="mt-10 flex items-center justify-between">
          <button
            type="button"
            data-testid="admissions-apply-back"
            onClick={() => setStepIdx(Math.max(0, stepIdx - 1))}
            disabled={stepIdx === 0}
            className="text-sm text-[#6B7280] hover:text-[#1A1B2E] disabled:opacity-30 inline-flex items-center gap-1.5"
          >
            <ArrowLeft size={14} />
            {t("admissions.apply.back")}
          </button>
          {stepIdx < STEPS.length - 1 ? (
            <button
              type="button"
              data-testid="admissions-apply-next"
              onClick={() => setStepIdx(stepIdx + 1)}
              className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-5 !text-sm"
            >
              {t("admissions.apply.next")}
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="admissions-apply-submit"
              onClick={submit}
              className="btn-primary inline-flex items-center gap-1.5 !py-2 !px-5 !text-sm"
            >
              <Check size={14} />
              {t("admissions.apply.submit")}
            </button>
          )}
        </div>
      </main>
    </div>
  );
}

function FullScreen({ children, ...rest }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div {...rest} className="max-w-md text-center">
        {children}
      </div>
    </div>
  );
}

// ── Step components ───────────────────────────────────────────────────────
function ChildStep({ data, onSave, t }) {
  const [form, setForm] = useState({
    name: data.name || "",
    dob: data.dob || "",
    home_language: data.home_language || "",
    comfort_items: (data.comfort_items || []).join(", "),
    favorite_song: data.favorite_song || "",
  });
  const sync = (k, v) => setForm({ ...form, [k]: v });
  const flush = () =>
    onSave({
      ...form,
      comfort_items: form.comfort_items
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    });
  return (
    <section data-testid="admissions-apply-step-child">
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_child")}</h2>
      <div className="mt-6 space-y-4">
        <FieldRow>
          <Field label="Child's name" required>
            <input data-testid="apply-child-name" type="text" required value={form.name} onChange={(e) => sync("name", e.target.value)} onBlur={flush} className="input-soft w-full" />
          </Field>
          <Field label="Date of birth" required>
            <input data-testid="apply-child-dob" type="date" required value={form.dob} onChange={(e) => sync("dob", e.target.value)} onBlur={flush} className="input-soft w-full" />
          </Field>
        </FieldRow>
        <Field label="Home language">
          <input data-testid="apply-child-language" type="text" value={form.home_language} onChange={(e) => sync("home_language", e.target.value)} onBlur={flush} className="input-soft w-full" placeholder="English, Spanish, …" />
        </Field>
        <Field label="Comfort items (comma-separated)">
          <input data-testid="apply-child-comforts" type="text" value={form.comfort_items} onChange={(e) => sync("comfort_items", e.target.value)} onBlur={flush} className="input-soft w-full" placeholder="blanket, small bear" />
        </Field>
        <Field label="Favorite song">
          <input data-testid="apply-child-song" type="text" value={form.favorite_song} onChange={(e) => sync("favorite_song", e.target.value)} onBlur={flush} className="input-soft w-full" />
        </Field>
      </div>
    </section>
  );
}

function MedicalStep({ data, onSave, t }) {
  const [form, setForm] = useState({
    allergies: (data.allergies || []).join(", "),
    conditions: (data.conditions || []).join(", "),
    dietary: (data.dietary || []).join(", "),
    doctor_name: data.doctor_name || "",
    doctor_phone: data.doctor_phone || "",
  });
  const sync = (k, v) => setForm({ ...form, [k]: v });
  const flush = () =>
    onSave({
      allergies: form.allergies.split(",").map((s) => s.trim()).filter(Boolean),
      conditions: form.conditions.split(",").map((s) => s.trim()).filter(Boolean),
      dietary: form.dietary.split(",").map((s) => s.trim()).filter(Boolean),
      doctor_name: form.doctor_name || null,
      doctor_phone: form.doctor_phone || null,
    });
  return (
    <section data-testid="admissions-apply-step-medical">
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_medical")}</h2>
      <p className="mt-2 text-sm text-[#6B7280]">Only what helps the team keep your child safe.</p>
      <div className="mt-6 space-y-4">
        <Field label="Allergies (comma-separated)">
          <input data-testid="apply-medical-allergies" type="text" value={form.allergies} onChange={(e) => sync("allergies", e.target.value)} onBlur={flush} className="input-soft w-full" placeholder="peanut, latex" />
        </Field>
        <Field label="Conditions">
          <input data-testid="apply-medical-conditions" type="text" value={form.conditions} onChange={(e) => sync("conditions", e.target.value)} onBlur={flush} className="input-soft w-full" placeholder="asthma" />
        </Field>
        <Field label="Dietary preferences">
          <input data-testid="apply-medical-dietary" type="text" value={form.dietary} onChange={(e) => sync("dietary", e.target.value)} onBlur={flush} className="input-soft w-full" placeholder="vegetarian, dairy-free" />
        </Field>
        <FieldRow>
          <Field label="Doctor's name">
            <input data-testid="apply-medical-doctor-name" type="text" value={form.doctor_name} onChange={(e) => sync("doctor_name", e.target.value)} onBlur={flush} className="input-soft w-full" />
          </Field>
          <Field label="Doctor's phone">
            <input data-testid="apply-medical-doctor-phone" type="tel" value={form.doctor_phone} onChange={(e) => sync("doctor_phone", e.target.value)} onBlur={flush} className="input-soft w-full" />
          </Field>
        </FieldRow>
      </div>
    </section>
  );
}

function ContactsStep({ data, kind, onSave, t }) {
  const [list, setList] = useState(
    data.length > 0
      ? data
      : [{ name: "", relationship: "", phone: "", email: "" }],
  );
  const update = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    setList(next);
  };
  const add = () => setList([...list, { name: "", relationship: "", phone: "", email: "" }]);
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  const flush = () =>
    onSave(list.filter((c) => c.name.trim() && c.phone.trim()));
  return (
    <section data-testid="admissions-apply-step-emergency" onBlur={flush}>
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_emergency")}</h2>
      <p className="mt-2 text-sm text-[#6B7280]">People we can reach if you can't be reached right away.</p>
      <ul className="mt-6 space-y-4">
        {list.map((c, i) => (
          <li key={i} className="rounded-2xl border border-[#EEF0F4] bg-[#F7F8FC] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="eyebrow">Contact #{i + 1}</span>
              {list.length > 1 && (
                <button type="button" onClick={() => remove(i)} className="text-[#B91C1C]">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <FieldRow>
              <Field label="Name" required>
                <input data-testid={`apply-emergency-name-${i}`} type="text" required value={c.name} onChange={(e) => update(i, { name: e.target.value })} className="input-soft w-full" />
              </Field>
              <Field label="Relationship">
                <input data-testid={`apply-emergency-rel-${i}`} type="text" value={c.relationship} onChange={(e) => update(i, { relationship: e.target.value })} className="input-soft w-full" />
              </Field>
            </FieldRow>
            <FieldRow>
              <Field label="Phone" required>
                <input data-testid={`apply-emergency-phone-${i}`} type="tel" required value={c.phone} onChange={(e) => update(i, { phone: e.target.value })} className="input-soft w-full" />
              </Field>
              <Field label="Email">
                <input data-testid={`apply-emergency-email-${i}`} type="email" value={c.email} onChange={(e) => update(i, { email: e.target.value })} className="input-soft w-full" />
              </Field>
            </FieldRow>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="apply-emergency-add"
        onClick={add}
        className="mt-4 text-sm font-semibold text-[#1F3A2A] inline-flex items-center gap-1.5"
      >
        <Plus size={14} />
        Add another contact
      </button>
    </section>
  );
}

function PickupStep({ data, onSave, t }) {
  const [list, setList] = useState(
    data.length > 0 ? data : [{ name: "", relationship: "", phone: "", id_required: true }],
  );
  const update = (i, patch) => {
    const next = [...list];
    next[i] = { ...next[i], ...patch };
    setList(next);
  };
  const add = () =>
    setList([...list, { name: "", relationship: "", phone: "", id_required: true }]);
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  const flush = () => onSave(list.filter((p) => p.name.trim() && p.phone.trim()));
  return (
    <section data-testid="admissions-apply-step-pickup" onBlur={flush}>
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_pickup")}</h2>
      <p className="mt-2 text-sm text-[#6B7280]">Trusted adults who may collect your child. ID will be requested.</p>
      <ul className="mt-6 space-y-4">
        {list.map((p, i) => (
          <li key={i} className="rounded-2xl border border-[#EEF0F4] bg-[#F7F8FC] p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="eyebrow">Person #{i + 1}</span>
              {list.length > 1 && (
                <button type="button" onClick={() => remove(i)} className="text-[#B91C1C]">
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <FieldRow>
              <Field label="Name" required>
                <input data-testid={`apply-pickup-name-${i}`} type="text" required value={p.name} onChange={(e) => update(i, { name: e.target.value })} className="input-soft w-full" />
              </Field>
              <Field label="Relationship">
                <input data-testid={`apply-pickup-rel-${i}`} type="text" value={p.relationship} onChange={(e) => update(i, { relationship: e.target.value })} className="input-soft w-full" />
              </Field>
            </FieldRow>
            <Field label="Phone" required>
              <input data-testid={`apply-pickup-phone-${i}`} type="tel" required value={p.phone} onChange={(e) => update(i, { phone: e.target.value })} className="input-soft w-full" />
            </Field>
          </li>
        ))}
      </ul>
      <button
        type="button"
        data-testid="apply-pickup-add"
        onClick={add}
        className="mt-4 text-sm font-semibold text-[#1F3A2A] inline-flex items-center gap-1.5"
      >
        <Plus size={14} />
        Add another person
      </button>
    </section>
  );
}

function ConsentsStep({ data, onSave, t }) {
  const [form, setForm] = useState({
    photo_in_app: data.photo_in_app ?? false,
    photo_in_marketing: data.photo_in_marketing ?? false,
    outdoor_play: data.outdoor_play ?? true,
    field_trips: data.field_trips ?? false,
    emergency_medical: data.emergency_medical ?? true,
    sunscreen: data.sunscreen ?? true,
  });
  const toggle = (k) => {
    const next = { ...form, [k]: !form[k] };
    setForm(next);
    onSave(next);
  };
  const items = [
    ["photo_in_app", "Share photos with me via the Alouvya app"],
    ["photo_in_marketing", "Use anonymized images in Alouvya marketing (you can revoke anytime)"],
    ["outdoor_play", "Daily supervised outdoor play"],
    ["field_trips", "Occasional supervised field trips"],
    ["emergency_medical", "Authorize emergency medical care when we cannot be reached"],
    ["sunscreen", "Apply child-safe sunscreen as needed"],
  ];
  return (
    <section data-testid="admissions-apply-step-consents">
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_consents")}</h2>
      <p className="mt-2 text-sm text-[#6B7280]">Granular. Tap to toggle.</p>
      <ul className="mt-6 space-y-3">
        {items.map(([k, label]) => (
          <li key={k}>
            <label
              data-testid={`apply-consent-${k}`}
              className="flex items-center justify-between rounded-2xl border border-[#EEF0F4] bg-white p-4 cursor-pointer hover:border-[#1F3A2A]/40 transition-colors"
            >
              <span className="text-sm text-[#1A1B2E]">{label}</span>
              <input
                type="checkbox"
                checked={form[k]}
                onChange={() => toggle(k)}
                className="h-5 w-5 accent-[#1F3A2A]"
              />
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DocumentsStep({ app, token, onSigned, t }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingDocId, setSigningDocId] = useState(null);
  const [typedName, setTypedName] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/admissions/apply/${token}/docs`);
        setDocs(r.data || []);
      } catch (e) {
        // graceful — empty list
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const required = app.required_signatures || [];
  const completed = new Set(app.completed_signatures || []);

  const sign = async (doc) => {
    if (typedName.trim().length < 2) {
      toast.error("Please type your full name.");
      return;
    }
    try {
      await api.post(`/admissions/apply/${token}/sign`, {
        doc_id: doc.id,
        typed_name: typedName.trim(),
      });
      toast.success(`Signed: ${doc.title}`);
      setSigningDocId(null);
      setTypedName("");
      onSigned();
    } catch (e) {
      toast.error("Could not sign. Please try again.");
    }
  };

  return (
    <section data-testid="admissions-apply-step-documents">
      <h2 className="font-display text-2xl text-[#1A1B2E]">{t("admissions.apply.step_documents")}</h2>
      <p className="mt-2 text-sm text-[#6B7280]">
        {required.length === 0
          ? "No documents to sign at this stage."
          : `${completed.size} of ${required.length} signed.`}
      </p>
      {required.length > 0 && (
        <ul className="mt-6 space-y-3" data-testid="apply-docs-list">
          {required.map((docId) => {
            const isSigned = completed.has(docId);
            const doc = docs.find((d) => d.id === docId);
            return (
              <li
                key={docId}
                data-testid={`apply-doc-${docId}`}
                className={
                  "rounded-2xl border p-5 " +
                  (isSigned
                    ? "border-[#1F3A2A] bg-[#E6EFE8]/40"
                    : "border-[#EEF0F4] bg-white")
                }
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="font-display text-lg text-[#1A1B2E]">
                      {doc?.title || "Required document"}
                    </h3>
                    {doc?.description && (
                      <p className="mt-1 text-sm text-[#6B7280]">{doc.description}</p>
                    )}
                    {doc?.body_markdown && signingDocId === docId && (
                      <div className="mt-4 max-h-64 overflow-y-auto rounded-xl bg-[#F7F8FC] border border-[#EEF0F4] p-4 text-sm whitespace-pre-wrap text-[#1A1B2E] leading-relaxed">
                        {doc.body_markdown}
                      </div>
                    )}
                  </div>
                  {isSigned ? (
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-[#1F3A2A] bg-[#D1F1D6] rounded-full px-2.5 py-1 inline-flex items-center gap-1">
                      <Check size={11} />
                      Signed
                    </span>
                  ) : (
                    <button
                      type="button"
                      data-testid={`apply-doc-sign-${docId}`}
                      onClick={() => setSigningDocId(docId === signingDocId ? null : docId)}
                      className="text-xs font-semibold text-[#1F3A2A] underline underline-offset-4"
                    >
                      Read & sign
                    </button>
                  )}
                </div>
                {!isSigned && signingDocId === docId && (
                  <div className="mt-4 space-y-3">
                    <label className="block">
                      <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
                        Type your full name as your signature
                      </span>
                      <input
                        data-testid={`apply-doc-typed-name-${docId}`}
                        type="text"
                        value={typedName}
                        onChange={(e) => setTypedName(e.target.value)}
                        className="input-soft w-full"
                        placeholder="Full legal name"
                      />
                    </label>
                    <p className="text-[11px] text-[#9CA3AF]">
                      By signing you acknowledge the contents of this document. We capture
                      your name, the date, your IP address, and a hash of the document body.
                    </p>
                    <button
                      type="button"
                      data-testid={`apply-doc-confirm-${docId}`}
                      onClick={() => sign(doc)}
                      className="btn-primary !py-2 !px-4 !text-sm"
                    >
                      Sign {doc?.title || ""}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

function FieldRow({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
        {label}
        {required && <span className="text-[#FF6B6B] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}
